import {
  AppointmentType,
  CommentTemplateType,
  ConsultationType,
  DateAppointment,
  DateFormatted,
  ObservationHistoryType,
  RemoteObservationType,
  ResultAnalyzesType,
} from '@/shared/types'
import { getDateFormat } from '@/shared/utils'
import { differenceInMonths } from 'date-fns'
import { DATE_FORMAT } from '@/shared/constants'

export class PlaqueReportController {
  private reportData

  public setReportData(reportData): void {
    this.reportData = reportData
    this.reportData.report.index = JSON.parse(this.reportData.report.index)
    if (this.reportData.observation_history === null) {
      this.reportData.observation_history = []
    } else {
      this.reportData.observation_history = this.parseObservationHistory(
        this.reportData.observation_history
      )
    }

    this.reportData = this.keysToCamel(reportData)
  }

  public getReportData() {
    return this.reportData
  }

  public getConsultationList(): ConsultationType {
    const consultation = {
      fullConsultationList: this.reportData.consultations,
      consultations: this.reportData.report.consultation
        ? JSON.parse(this.reportData.report.consultation).map((consultation) =>
            this.toCamel(consultation)
          )
        : [],
      reCleaning: this.reportData.report.reCleaning ?? 0,
    }

    return consultation
  }

  public getCommentsTemplates(): CommentTemplateType[] {
    return this.reportData.commentTemplates.map((template) => {
      return {
        template_id: template.id,
        template: template.template,
        isVisible: false,
      }
    })
  }

  public async getAnalyzesResult(): Promise<ResultAnalyzesType> {
    const resultAnalyzes = this.reportData.report
    const indexData = resultAnalyzes?.index

    if (indexData) {
      indexData.day = indexData.day * 100
      indexData.long = (indexData.month + indexData.week) * 100
      indexData.greenVermillion = indexData.greenVermillion ?? 0
      indexData.apiLange = indexData.apiLange ? indexData.apiLange * 100 : 0
      indexData.fedorovVolodkina = indexData.fedorovVolodkina ?? 0
      indexData.php = indexData.php ?? 0
      indexData.cfe = {
        caries: resultAnalyzes.caries,
        filling: resultAnalyzes.filling,
        extracted: resultAnalyzes.extracted,
      }
    } else {
      resultAnalyzes.index = {
        apiLange: 0,
        color: '',
        day: 0,
        long: 0,
        fedorovVolodkina: 0,
        greenVermillion: 0,
        php: 0,
        cfe: {
          caries: null,
          filling: null,
          extracted: null,
        },
      }
    }

    const teethStoneId = JSON.parse(resultAnalyzes.stone)
    resultAnalyzes.stoneList = []
    resultAnalyzes.color = this.reportData.color

    if (teethStoneId) {
      teethStoneId.forEach((index) => {
        resultAnalyzes.stoneList.push(this.getSignForStoneStatus(index))
      })
    }

    resultAnalyzes.isPigmented = resultAnalyzes.pigmented === 1

    return resultAnalyzes
  }

  public getSignForStoneStatus(teethStoneId: string): string {
    switch (teethStoneId) {
      case '1':
        return 'Зубной камень отсутствует'
      case '2':
        return 'Наддесневой до 1/3 коронки'
      case '3':
        return 'Наддесневой от 1/3 до 2/3 коронки'
      case '4':
        return 'Наддесневой более чем 2/3 коронки'
      case '5':
        return 'Поддесневой, окружающий пришеечную часть зуба'
      default:
        return ''
    }
  }

  public getObservationHistory(): ObservationHistoryType[] {
    return this.reportData.observationHistory.map((historyItem) => {
      const { dateAt, index, id } = historyItem
      const day = index?.day ?? 0
      const month = index?.month ?? 0
      const week = index?.week ?? 0

      if (dateAt) {
        return {
          dateAt,
          day: day * 100,
          long: (month + week) * 100,
          id,
        }
      }
    })
  }

  public getCfeValues() {
    if (this.reportData?.report?.index?.cfe) {
      return this.reportData.report.index.cfe
    }

    return { caries: '', filling: '', extracted: '' }
  }

  public updateCfeValues(caries: number, filling: number, extracted: number) {
    this.reportData.report.index.cfe = {
      caries,
      filling,
      extracted,
    }
  }

  public getAppointments(): AppointmentType {
    const dates: DateAppointment[] = this.getAppointmentDates()
    const levels = this.getAppointmentValues('levelRounded')
    const differencesLevels = this.getAppointmentValues('difference')
    const spaceBetweenMonths = this.getSpaceBetweenMonths()
    const formattedShortDates = dates.map((date) =>
      this.formatedDate(date, DATE_FORMAT.myShort)
    )

    const formattedLongDates = dates
      .map((date) => this.formatedDate(date, DATE_FORMAT.mydLong))
      .slice(1)

    const filledShortDates = this.fillDataEmptyValues(
      formattedShortDates,
      spaceBetweenMonths
    )
    const filledLongDates = this.fillDataEmptyValues(
      formattedLongDates,
      spaceBetweenMonths
    )
    const filledLevels = this.fillDataEmptyValues(levels, spaceBetweenMonths)
    const filledDifferencesLevels = this.fillDataEmptyValues(
      differencesLevels,
      spaceBetweenMonths
    )

    return {
      shortDates: filledShortDates,
      longDates: filledLongDates,
      levels: filledLevels,
      differences: filledDifferencesLevels,
      spacesValues: spaceBetweenMonths,
    }
  }

  public getSpaceBetweenMonths(): number[] {
    const dates: DateAppointment[] = this.getAppointmentDates()
    return this.computeDifferencesBetweenMonths(dates)
  }

  public getFormatedDateForPDF(date: Date, dateFormat: string): string {
    const formatedDate = getDateFormat(date, dateFormat)
    const splitFormatedDate = formatedDate.split(' ')
    const nameMonth = splitFormatedDate[0]
    const year = splitFormatedDate[1] + '`'

    return `${this.getWordWithCapitalLetter(nameMonth)} ${year}`
  }

  public getAppointmentsYears(): [string, string] {
    const years = this.reportData.appointments
      .map((point) => point.dateAt.split('-')[0])
      .slice(1)

    const filteredYears = Array.from<string>(new Set(years))
    filteredYears.length = 2

    const indexStartFirstYear = 0
    const indexNextYear = 2
    const slicedYears = filteredYears
      .sort()
      .slice(indexStartFirstYear, indexNextYear)

    return [slicedYears[0], slicedYears[1]]
  }

  public getMonths(): string[] {
    const dates = this.reportData.appointments.map((point) => point.dateAt)

    return dates.map((date) => {
      return getDateFormat(new Date(date), 'LLLL')
    })
  }

  public getFirstDateAppointment(): string {
    const firstDate = new Date(this.reportData.appointments[0].dateAt)
    const firstDateFormatted = getDateFormat(firstDate, DATE_FORMAT.dmyAllShort)
    const splittedFirstDate = firstDateFormatted.split(' ')

    return `${splittedFirstDate[0]} ${this.getWordWithCapitalLetter(
      splittedFirstDate[1]
    )} ${splittedFirstDate[2]}`
  }

  private getWordWithCapitalLetter(word: string): string {
    const firstLetter = word[0]
    const capitalFirstLetter = firstLetter.toUpperCase()

    return capitalFirstLetter + word.slice(1)
  }

  private computeDifferencesBetweenMonths(dates: DateAppointment[]): number[] {
    const DISTANT_ADJACENT_MONTHS = 1
    const indexPenultimateItem = dates.length - 2
    const distancesBetweenMonths: number[] = []

    for (let indexItem = 0; indexItem <= indexPenultimateItem; indexItem++) {
      const indexNextItem = indexItem + DISTANT_ADJACENT_MONTHS

      const distanceBetweenMonths = differenceInMonths(
        dates[indexNextItem].dateAt,
        dates[indexItem].dateAt
      )

      distancesBetweenMonths.push(distanceBetweenMonths)
    }

    return distancesBetweenMonths
  }

  private fillDataEmptyValues<T>(
    data: Array<T>,
    distancesBetweenMonths: number[]
  ): Array<T | null> {
    const filledData = []

    for (const distance of distancesBetweenMonths) {
      filledData.push(data.shift())

      for (let i = 0; i < distance - 1; i++) {
        filledData.push(null)
      }
    }

    filledData.push(data.shift())

    return filledData
  }

  private formatedDate(
    date: DateAppointment,
    dateFormat: string
  ): DateFormatted {
    const dateFormatted = this.getFormatedDateForPDF(date.dateAt, dateFormat)

    return {
      date: dateFormatted,
      value: date.level,
    }
  }

  private getAppointmentDates(): DateAppointment[] {
    return this.reportData.appointments.map((point) => {
      return {
        dateAt: new Date(point.dateAt),
        level: point.level,
      }
    })
  }

  private getAppointmentValues(
    typeValue: 'levelRounded' | 'difference'
  ): number[] {
    return this.reportData.appointments.map((point) => point[typeValue])
  }

  private parseObservationHistory(
    observationHistory: RemoteObservationType[]
  ): unknown {
    return observationHistory.map((historyItem) => {
      historyItem.index = JSON.parse(historyItem.index)
      return historyItem
    })
  }

  private keysToCamel(o: object | []): object {
    if (this.isObject(o)) {
      const n = {}

      Object.keys(o).forEach((k) => {
        n[this.toCamel(k)] = this.keysToCamel(o[k])
      })

      return n
    } else if (this.isArray(o)) {
      // @ts-ignore
      return o.map((i) => {
        return this.keysToCamel(i)
      })
    }

    return o
  }

  private toCamel(s: string): string {
    return s.replace(/([-_][a-z])/gi, ($1) => {
      return $1.toUpperCase().replace('-', '').replace('_', '')
    })
  }

  private isArray(a: object): boolean {
    return Array.isArray(a)
  }

  private isObject(o: object): boolean {
    return o === Object(o) && !this.isArray(o) && typeof o !== 'function'
  }
}
